'use client'

import { useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getPromptComments } from '@/lib/vault/prompts/comment-thread-actions'
import { useSseChannel } from '@/lib/sse/use-sse-channel'
import type { Comment, CreateCommentResult } from '@/types/comment.types'
import type { ActionResult } from '@/types/actions'

async function fetchComments(promptId: string): Promise<Comment[]> {
  const result = await getPromptComments(promptId)
  if (!result.success) throw new Error(result.error)
  return result.data ?? []
}

interface CurrentUser {
  id: string
  username: string
  avatarUrl: string | null
}

interface UseCommentThreadOptions {
  promptId: string
  initialData?: Comment[]
  currentUser?: CurrentUser
}

export function useCommentThread({ promptId, initialData, currentUser }: UseCommentThreadOptions) {
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => ['comments', promptId], [promptId])

  const commentsQuery = useQuery({
    queryKey,
    queryFn: () => fetchComments(promptId),
    initialData,
    retry: 2,
    staleTime: 60_000,
  })

  // Realtime: invalidate the thread when any comment row changes for
  // this prompt. The SSE payload doesn't include the row body, so we
  // refetch via the existing query.
  const onCommentEvent = useCallback(
    (event: { type: string }) => {
      if (event.type !== 'message') return
      queryClient.invalidateQueries({ queryKey })
    },
    [queryClient, queryKey],
  )

  useSseChannel(
    promptId ? `prompt:${promptId}:comments` : null,
    onCommentEvent,
    { enabled: !!promptId },
  )

  const addCommentMutation = useMutation({
    mutationFn: async ({
      content,
      parentCommentId,
      createComment,
    }: {
      content: string
      parentCommentId?: string
      createComment: (fd: FormData) => Promise<ActionResult<CreateCommentResult>>
    }) => {
      const fd = new FormData()
      fd.set('promptId', promptId)
      fd.set('content', content)
      if (parentCommentId) fd.set('parentCommentId', parentCommentId)
      return createComment(fd)
    },
    onMutate: async ({ content, parentCommentId }) => {
      // Cancel any in-flight refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey })

      const previous = queryClient.getQueryData<Comment[]>(queryKey)

      if (currentUser) {
        const optimistic: Comment = {
          id: `optimistic-${Date.now()}`,
          prompt_id: promptId,
          user_id: currentUser.id,
          author_username: currentUser.username,
          author_avatar_url: currentUser.avatarUrl,
          content,
          parent_comment_id: parentCommentId ?? null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          replies: [],
          reactions: [],
        }

        queryClient.setQueryData<Comment[]>(queryKey, (old = []) => {
          if (parentCommentId) {
            // Add as a reply to the parent comment
            return old.map((c) =>
              c.id === parentCommentId
                ? { ...c, replies: [...c.replies, optimistic] }
                : c,
            )
          }
          return [...old, optimistic]
        })
      }

      return { previous }
    },
    onError: (_err, _vars, context) => {
      // Rollback to previous state on error
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
    },
  })

  return {
    comments: commentsQuery.data ?? [],
    isLoading: commentsQuery.isLoading,
    error: commentsQuery.error,
    addComment: addCommentMutation.mutateAsync,
    isAdding: addCommentMutation.isPending,
  }
}
